import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { NXEyes } from "../../icons";
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/client';

import UserDetails from "./UserDetails";
import UserPermissions, { PermissionHierarchyModal } from "./permissions";


import GenericSelectableTable from "../selectableTable";
import { GraphQLPagination } from "../common";
import { getQueryVariables } from "../../utils";
import { toast } from "react-toastify";



const GET_USERS = gql`
  query GetUsers($first: Int, $last: Int, $after: String, $before: String) {
    users(first: $first, last: $last, after: $after, before: $before) {
      edges {
        node {
          dbId
          dateJoined
          fullName
          username
          isSuperuser
          isStoreStaff
          isStoreAdmin
          isActive
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;


function UserTable({ users }: { users: any[]}) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openUserDetails, setOpenUserDetails] = useState<any>(null);
  const [permission, setPermission] = useState<any>(null);

  const { loading, error, data, refetch } = useQuery(GET_USERS, {
    variables: getQueryVariables(searchParams),
    onCompleted: (data) => {

    },
    onError: (error) => {
        console.error("Error retrieving users:", error);
    },
  });

  useEffect(() => {
    if (!searchParams.get("first") && !searchParams.get("last")) {
      setSearchParams({ first: "20" });
    }
  }, []);

  const handleAccessControl = (row:any) => {

    if (row.node.isSuperuser || row.node.isStoreAdmin) {
      toast.error("Cannot change permissions for store admin or superuser as they have full access to the system.");
      return;
    }

    setPermission(parseInt(row.node.dbId, 10));
  }

  // Define the columns for the table (adjust the names to match your data)
  const columns = [
    { header: "User", key: "node.dbId", render: (row: any) =>  {
      return (
        <div className="flex items-center space-x-2">
            <a
            href="#"
            className="font-semibold text-blue-500 hover:underline"
            onClick={(e) => {
              e.preventDefault();
              setOpenUserDetails(parseInt(row.node.dbId, 10));
            }}
            >
            @{row.node.username}
            </a>
          <span className="text-gray-500">#D{row.node.dbId}</span>
        </div>
      )
    } },
    { header: "Name", key: "node.fullName" },
    { header: "Permission Level", key: "", render: (row: any) => {
      return (
        <a
          href="#"
          className="px-2 py-1 rounded-full font-semibold text-sm"
          onClick={(e) => {
            e.preventDefault();
            setIsHelpOpen(true);
          }}
        >
          {!row.node.isActive ? (
            <span className="bg-gray-200 text-gray-600 border border-gray-400 rounded-full px-2 py-1">Deactivated</span>
          ) : row.node.isSuperuser ? (
            <span className="bg-red-100 text-red-700 border border-red-400 rounded-full px-2 py-1">Superuser</span>
          ) : row.node.isStoreAdmin ? (
            <span className="bg-blue-100 text-blue-700 border border-blue-400 rounded-full px-2 py-1">Store Admin</span>
          ) : row.node.isStoreStaff ? (
            <span className="bg-yellow-100 text-yellow-700 border border-yellow-400 rounded-full px-2 py-1">Store Staff</span>
          ) : (
            <span className="bg-gray-200 text-gray-600 border border-gray-400 rounded-full px-2 py-1">Invalid Staff</span>
          )}
        </a>
      )
    }},
    { header: "Actions", key: "actions", render: (row: any) => {
      return (
        <div className="flex gap-2 items-center">
            <button 
              onClick={() => handleAccessControl(row)} 
              className={`px-3 py-1 rounded underline ${
              !row.node.isActive ? "bg-gray-200 text-gray-400 cursor-not-allowed" : 
              (row.node.isSuperuser || row.node.isStoreAdmin) ? "bg-gray-100 text-gray-500" : 
              "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
              }`}
              disabled={!row.node.isActive}
            >
              Access Control
            </button>
        </div>
      )
    } },
  ];



  return (
    <div className="relative overflow-x-auto">
      {/* Pass columns and data to GenericSelectableTable */}
      <GenericSelectableTable
        columns={columns}
        data={data?.users?.edges || []}
      />

      <GraphQLPagination 
        edges={data?.users?.edges || []}
        pageInfo={data?.users?.pageInfo || {}}
      />


      {openUserDetails && (
        <UserDetails onDeactivate={refetch} isOpen={openUserDetails ? true : false} onClose={() => setOpenUserDetails(null)} id={openUserDetails} />
      )}

     

      {permission && (
        <UserPermissions isOpen={permission ? true : false} onClose={() => setPermission(null)} id={permission} />
      )}

       <PermissionHierarchyModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

    </div>
  );
}

export default UserTable;
